import React from 'react';
import { Box, Divider, List, ListItem, ListItemIcon, IconButton, Tooltip, Button, Stack, Typography, Grid, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import TableViewIcon from '@mui/icons-material/TableView';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DatasetIcon from '@mui/icons-material/Dataset';
import ExtensionIcon from '@mui/icons-material/Extension';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ApiIcon from '@mui/icons-material/Api';

interface TableType {
  id?: string;
  _id?: string;
  name: string;
  description: string;
  rowCount?: number;
  supportsVariations?: boolean;
  sourceType?: 'woocommerce' | 'csv-upload';
  originalFileName?: string;
}

interface ExistingTablesProps {
  tables: TableType[];
  syncingTable: string | null;
  onEditDescription: (table: TableType) => void;
  onSyncTable: (table: TableType) => void;
  onViewTable: (table: TableType) => void;
  onGenerateQA: (table: TableType) => void;
  onDeleteTable: (table: TableType) => void;
  onAddTableClick: () => void;
}

// Helper function to truncate description to first paragraph only
const truncateToFirstParagraph = (description: string): string => {
  if (!description || description.trim().length === 0) {
    return description;
  }
  
  // Look for the pattern "**Table Description**\n[content]\n\n**Column Descriptions**"
  const columnDescriptionMatch = description.match(/\*\*Column Descriptions?\*\*/i);
  
  if (columnDescriptionMatch) {
    // Extract content before "**Column Descriptions**"
    const beforeColumnSection = description.substring(0, columnDescriptionMatch.index).trim();
    
    // Remove "**Table Description**" header if present at the start
    const tableDescMatch = beforeColumnSection.match(/^\*\*Table Description\*\*\s*\n?(.*)$/s);
    if (tableDescMatch) {
      return tableDescMatch[1].trim();
    }
    
    return beforeColumnSection;
  }
  
  // Fallback: If no column section found, look for first paragraph (double newline)
  const firstParagraphMatch = description.match(/^(.*?)(?:\n\s*\n|$)/s);
  if (firstParagraphMatch) {
    // Remove any markdown headers from the first paragraph
    return firstParagraphMatch[1]
      .replace(/^\*\*Table Description\*\*\s*\n?/i, '')
      .trim();
  }
  
  // Final fallback: return the whole description if no clear structure
  return description;
};

// Helper function to get source type badge
const getSourceBadge = (table: TableType) => {
  const sourceType = table.sourceType || 'woocommerce';
  const originalFileName = table.originalFileName;
  
  if (sourceType === 'csv-upload') {
    return (
      <Chip 
        icon={<UploadFileIcon />}
        label={`CSV Upload: ${originalFileName || 'Unknown file'}`} 
        color="success" 
        size="small" 
        variant="outlined"
        sx={{ mr: 1, fontWeight: 500 }}
      />
    );
  } else {
    return (
      <Chip 
        icon={<ApiIcon />}
        label="WooCommerce API" 
        color="primary" 
        size="small" 
        variant="outlined"
        sx={{ mr: 1, fontWeight: 500 }}
      />
    );
  }
};

// Helper function to get the appropriate icon for table source
const getSourceIcon = (table: TableType) => {
  const sourceType = table.sourceType || 'woocommerce';
  
  if (sourceType === 'csv-upload') {
    return <UploadFileIcon color="success" />;
  } else {
    return <StorageIcon color="primary" />;
  }
};

const ExistingTables: React.FC<ExistingTablesProps> = ({
  tables,
  syncingTable,
  onEditDescription,
  onSyncTable,
  onViewTable,
  onGenerateQA,
  onDeleteTable,
  onAddTableClick
}) => (
  <>
    <List>
      {tables.map((table) => {
        const tableId = table.id || table._id || '';
        return (
          <ListItem 
            key={tableId}
            alignItems="flex-start"
            sx={{ 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              py: 2
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={7} md={8} lg={9}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    {getSourceIcon(table)}
                  </ListItemIcon>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" component="span">
                        {table.name}
                      </Typography>
                      {getSourceBadge(table)}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ pr: 2, mb: 1 }}>
                      {truncateToFirstParagraph(table.description)}
                    </Typography>
                    
                    {/* Row count, variations info, and database ID */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<DatasetIcon />}
                        label={`${(table.rowCount || 0).toLocaleString()} rows`}
                        size="small"
                        variant="outlined"
                        color={table.rowCount && table.rowCount > 0 ? "primary" : "default"}
                        sx={{ 
                          minWidth: '100px',
                          '& .MuiChip-icon': { fontSize: '0.875rem' }
                        }}
                      />
                      {table.supportsVariations && (
                        <Chip
                          icon={<ExtensionIcon />}
                          label="Has Variations"
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ 
                            '& .MuiChip-icon': { fontSize: '0.875rem' }
                          }}
                        />
                      )}
                      <Tooltip title="Database ID" arrow>
                        <Chip
                          icon={<FingerprintIcon />}
                          label={tableId}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ 
                            fontFamily: 'monospace',
                            maxWidth: '200px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            },
                            '& .MuiChip-icon': { fontSize: '0.875rem' }
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={5} md={4} lg={3}>
                <Stack 
                  direction="row" 
                  spacing={1} 
                  justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                  sx={{ mt: { xs: 1, sm: 0 } }}
                >
                  <Tooltip title="Edit Description">
                    <IconButton size="small" onClick={() => onEditDescription(table)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Only show sync button for WooCommerce tables */}
                  {(table.sourceType || 'woocommerce') === 'woocommerce' && (
                    <Tooltip title="Sync Database">
                      <IconButton 
                        size="small"
                        onClick={() => onSyncTable(table)} 
                        disabled={syncingTable === tableId}
                      >
                        <SyncIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="View Data">
                    <IconButton size="small" onClick={() => onViewTable(table)}>
                      <TableViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate Q&A">
                    <IconButton size="small" onClick={() => onGenerateQA(table)}>
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="Custom SQL">
                    <IconButton size="small" onClick={() => onViewTable(table)}>
                      <LanguageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="Delete Database">
                    <IconButton size="small" onClick={() => onDeleteTable(table)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </ListItem>
        );
      })}
    </List>
    <Divider sx={{ my: 2 }} />
    <Box textAlign="center">
      <Button startIcon={<AddIcon />} variant="outlined" onClick={onAddTableClick}>
        Add New Database
      </Button>
    </Box>
  </>
);

export default ExistingTables;